import React, { useState, useEffect, useRef } from 'react';
import { CreditCard, X, Loader2 } from 'lucide-react';
import {
  PaymentIntentResponse,
  CardBillingInfo,
  emptyCardBillingInfo,
  getAvailableCountries,
  getSubdivisions,
  getStateLabel,
} from './billingTypes';
import SearchableSelect from './SearchableSelect';

declare global {
  interface Window {
    Stripe: any;
    Accept: any;
  }
}

interface PaymentMethodFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: PaymentFormSubmitData) => Promise<void>;
  paymentIntent: PaymentIntentResponse | null;
  serverRegion: string;
  initialBillingInfo?: CardBillingInfo;
  loading?: boolean;
}

export interface PaymentFormSubmitData {
  gateway: 'stripe' | 'authnet';
  stripeElements?: any;
  stripeInstance?: any;
  clientSecret?: string;
  authnetCard?: {
    cardNumber: string;
    expMonth: string;
    expYear: string;
    cvv: string;
  };
  billingInfo: CardBillingInfo;
}

const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({
  isOpen,
  onClose,
  onSave,
  paymentIntent,
  serverRegion,
  initialBillingInfo,
  loading = false,
}) => {
  const [billingInfo, setBillingInfo] = useState<CardBillingInfo>(
    initialBillingInfo || emptyCardBillingInfo(serverRegion)
  );
  const [authnetCard, setAuthnetCard] = useState({
    cardNumber: '',
    expMonth: '',
    expYear: '',
    cvv: '',
  });

  const [stripeElements, setStripeElements] = useState<any>(null);
  const [stripeInstance, setStripeInstance] = useState<any>(null);
  const cardContainerRef = useRef<HTMLDivElement>(null);

  const [saveHovered, setSaveHovered] = useState(false);
  const [cancelHovered, setCancelHovered] = useState(false);
  const [closeHovered, setCloseHovered] = useState(false);

  const availableCountries = getAvailableCountries(serverRegion);
  const subs = getSubdivisions(billingInfo.country);

  useEffect(() => {
    if (!isOpen || !paymentIntent) return;

    if (paymentIntent.gateway === 'stripe' && cardContainerRef.current && !stripeElements) {
      initStripeForm();
    }

    if (initialBillingInfo) {
      setBillingInfo(initialBillingInfo);
    }
  }, [isOpen, paymentIntent, initialBillingInfo]);

  useEffect(() => {
    if (!isOpen) {
      setStripeElements(null);
      setStripeInstance(null);
      setAuthnetCard({ cardNumber: '', expMonth: '', expYear: '', cvv: '' });
    }
  }, [isOpen]);

  const initStripeForm = async () => {
    if (!cardContainerRef.current || !window.Stripe || !paymentIntent?.clientSecret || !paymentIntent?.stripePublishableKey) return;

    const stripe = window.Stripe(paymentIntent.stripePublishableKey);
    setStripeInstance(stripe);

    const elements = stripe.elements();
    cardContainerRef.current.innerHTML = '';

    const cardElement = elements.create('card', {
      // Postal code comes from our own field (alphanumeric, so non-US formats work).
      // disableLink suppresses the Stripe Link prompt. Same options as BillingAccountCore.tsx.
      hidePostalCode: true,
      disableLink: true,
      style: {
        base: {
          fontSize: '16px',
          color: 'var(--v2-text)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          '::placeholder': { color: '#94a3b8' },
        },
      },
    });

    cardElement.mount(cardContainerRef.current);
    setStripeElements(elements.getElement('card'));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!paymentIntent) return;

    const submitData: PaymentFormSubmitData = {
      gateway: paymentIntent.gateway,
      billingInfo,
    };

    if (paymentIntent.gateway === 'stripe') {
      submitData.stripeElements = stripeElements;
      submitData.stripeInstance = stripeInstance;
      submitData.clientSecret = paymentIntent.clientSecret;
    } else {
      submitData.authnetCard = authnetCard;
    }

    await onSave(submitData);
  };

  if (!isOpen) return null;

  // Styles
  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: 'var(--v2-surface)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-lg)',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'var(--font-sans)',
  };

  const headerStyle: React.CSSProperties = {
    padding: '20px 24px',
    borderBottom: '1px solid var(--v2-border)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: 'var(--v2-text)',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  };

  const bodyStyle: React.CSSProperties = {
    padding: '24px',
    overflowY: 'auto',
    flex: 1,
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: 'var(--v2-text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '12px',
    marginTop: '20px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: 'var(--v2-text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '6px',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'var(--v2-surface)',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '0.875rem',
    color: 'var(--v2-text)',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  };

  const stripeContainerStyle: React.CSSProperties = {
    padding: '12px 14px',
    backgroundColor: 'var(--v2-surface)',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-sm)',
    minHeight: '44px',
  };

  const fieldGroupStyle: React.CSSProperties = {
    marginBottom: '14px',
  };

  const rowStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  };

  const footerStyle: React.CSSProperties = {
    padding: '16px 24px',
    borderTop: '1px solid var(--v2-border)',
    backgroundColor: 'var(--v2-surface-alt)',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  };

  const buttonStyle = (
    primary: boolean,
    hovered: boolean,
    disabled: boolean
  ): React.CSSProperties => ({
    padding: '10px 20px',
    backgroundColor: disabled
      ? 'var(--v2-border)'
      : primary
        ? hovered
          ? 'var(--v2-primary-hover)'
          : 'var(--v2-primary)'
        : hovered
          ? 'var(--v2-surface-hover)'
          : 'var(--v2-surface)',
    color: disabled ? 'var(--v2-text-muted)' : primary ? '#ffffff' : 'var(--v2-text)',
    border: primary ? 'none' : '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  });

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = 'var(--v2-primary)';
    e.target.style.boxShadow = '0 0 0 3px var(--v2-primary-muted)';
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = 'var(--v2-border)';
    e.target.style.boxShadow = 'none';
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <h2 style={titleStyle}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--v2-primary-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CreditCard size={20} strokeWidth={1.5} color="var(--v2-primary)" />
            </div>
            Add Payment Method
          </h2>
          <button
            onClick={onClose}
            onMouseEnter={() => setCloseHovered(true)}
            onMouseLeave={() => setCloseHovered(false)}
            style={{
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: closeHovered ? 'var(--v2-surface-hover)' : 'transparent',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              color: 'var(--v2-text-muted)',
              transition: 'background-color 0.15s ease',
            }}
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        {/* Body */}
        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}
        >
          <div style={bodyStyle}>
            {/* Cardholder Name */}
            <div style={rowStyle}>
              <div style={fieldGroupStyle}>
                <label style={labelStyle}>First Name *</label>
                <input
                  type="text"
                  value={billingInfo.firstName}
                  onChange={(e) => setBillingInfo({ ...billingInfo, firstName: e.target.value })}
                  style={inputStyle}
                  required
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
              </div>
              <div style={fieldGroupStyle}>
                <label style={labelStyle}>Last Name *</label>
                <input
                  type="text"
                  value={billingInfo.lastName}
                  onChange={(e) => setBillingInfo({ ...billingInfo, lastName: e.target.value })}
                  style={inputStyle}
                  required
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
              </div>
            </div>

            {/* Card Details Section */}
            <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>Card Details</div>

            {paymentIntent?.gateway === 'stripe' ? (
              <div style={fieldGroupStyle}>
                <div ref={cardContainerRef} style={stripeContainerStyle} />
              </div>
            ) : (
              <>
                <div style={fieldGroupStyle}>
                  <label style={labelStyle}>Card Number *</label>
                  <input
                    type="text"
                    value={authnetCard.cardNumber}
                    onChange={(e) =>
                      setAuthnetCard({
                        ...authnetCard,
                        cardNumber: e.target.value.replace(/\D/g, '').slice(0, 16),
                      })
                    }
                    placeholder="4111 1111 1111 1111"
                    style={inputStyle}
                    required
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '80px 100px 80px', gap: '12px' }}>
                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>MM *</label>
                    <input
                      type="text"
                      value={authnetCard.expMonth}
                      onChange={(e) =>
                        setAuthnetCard({
                          ...authnetCard,
                          expMonth: e.target.value.replace(/\D/g, '').slice(0, 2),
                        })
                      }
                      placeholder="MM"
                      style={inputStyle}
                      required
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                    />
                  </div>
                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>YYYY *</label>
                    <input
                      type="text"
                      value={authnetCard.expYear}
                      onChange={(e) =>
                        setAuthnetCard({
                          ...authnetCard,
                          expYear: e.target.value.replace(/\D/g, '').slice(0, 4),
                        })
                      }
                      placeholder="YYYY"
                      style={inputStyle}
                      required
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                    />
                  </div>
                  <div style={fieldGroupStyle}>
                    <label style={labelStyle}>CVV *</label>
                    <input
                      type="password"
                      value={authnetCard.cvv}
                      onChange={(e) =>
                        setAuthnetCard({
                          ...authnetCard,
                          cvv: e.target.value.replace(/\D/g, '').slice(0, 4),
                        })
                      }
                      placeholder="•••"
                      style={inputStyle}
                      required
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Billing Address Section */}
            <div style={sectionTitleStyle}>Billing Address</div>

            {/* Country */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Country *</label>
              <SearchableSelect
                value={billingInfo.country}
                onChange={(code) => setBillingInfo({ ...billingInfo, country: code, state: '' })}
                options={availableCountries}
                placeholder="Select country"
              />
            </div>

            {/* Address */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Address *</label>
              <input
                type="text"
                value={billingInfo.addressLine1}
                onChange={(e) => setBillingInfo({ ...billingInfo, addressLine1: e.target.value })}
                style={inputStyle}
                required
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>

            {/* City */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>City *</label>
              <input
                type="text"
                value={billingInfo.city}
                onChange={(e) => setBillingInfo({ ...billingInfo, city: e.target.value })}
                style={inputStyle}
                required
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>

            {/* State & Postal */}
            <div style={rowStyle}>
              <div style={fieldGroupStyle}>
                <label style={labelStyle}>{getStateLabel(billingInfo.country)}</label>
                {subs ? (
                  <SearchableSelect
                    value={billingInfo.state}
                    onChange={(code) => setBillingInfo({ ...billingInfo, state: code })}
                    options={subs}
                    placeholder="Select…"
                    openUp
                  />
                ) : (
                  <input
                    type="text"
                    value={billingInfo.state}
                    onChange={(e) => setBillingInfo({ ...billingInfo, state: e.target.value })}
                    style={inputStyle}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                  />
                )}
              </div>
              <div style={fieldGroupStyle}>
                <label style={labelStyle}>Postal Code</label>
                <input
                  type="text"
                  value={billingInfo.postalCode}
                  onChange={(e) => setBillingInfo({ ...billingInfo, postalCode: e.target.value })}
                  style={inputStyle}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={footerStyle}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              onMouseOver={() => setCancelHovered(true)}
              onMouseOut={() => setCancelHovered(false)}
              style={buttonStyle(false, cancelHovered, loading)}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              onMouseOver={() => setSaveHovered(true)}
              onMouseOut={() => setSaveHovered(false)}
              style={buttonStyle(true, saveHovered, loading)}
            >
              {loading && <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />}
              {loading ? 'Processing...' : 'Save Card'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentMethodForm;
